import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import BarChartComponent from "../../components/results/barChartComponent";
import hbAnalyticsApi from "../../api/hbAnalyticsApi";
import questionnaireScopeApi from "../../api/questionnaireScopeApi";
import responsesApi from "../../api/responsesApi";

import ageRanges from "../../data/ageRanges";
import mexicoStates from "../../data/mexicoStates";
import workScheme from "../../data/workScheme";
import biologicalSexList from "../../data/biologicalSex";
import jobArea from "../../data/jobArea";

import styles from "./HraAnalyticsPage.module.css";

const pickFirst = (...values) =>
  values.find((v) => v !== undefined && v !== null);

const asSeries = (value) => (Array.isArray(value) ? value : []);

const normalizeHraAnalytics = (raw) => {
  const source = pickFirst(
    raw?.hra,
    raw?.data?.hra,
    raw?.data?.analytics,
    raw?.data?.hraAnalytics,
    raw?.analytics,
    raw?.hraAnalytics,
    raw?.data,
    raw
  );
  return {
    totalRespondents: pickFirst(raw?.totalRespondents, source?.totalRespondents),
    metabolicRisk: asSeries(source?.metabolicRisk),
    nutritionalState: asSeries(source?.nutritionalState),
    lifeStyle: asSeries(source?.lifeStyle),
    physicalActivityEvaluation: asSeries(source?.physicalActivityEvaluation),
    workHappiness: asSeries(source?.workHappiness),
    overallEvaluation: asSeries(source?.overallEvaluation)
  };
};

const normalizeStressAnalytics = (raw) => {
  const source = pickFirst(
    raw?.stress,
    raw?.data?.stress,
    raw?.data?.analytics,
    raw?.data?.stressAnalytics,
    raw?.analytics,
    raw?.stressAnalytics,
    raw?.data,
    raw
  );
  return {
    totalRespondents: pickFirst(raw?.totalRespondents, source?.totalRespondents),
    hasBurnOut: asSeries(source?.hasBurnOut),
    exhaustionLevel: asSeries(source?.exhaustionLevel),
    cynicismLevel: asSeries(source?.cynicismLevel),
    efficacyLevel: asSeries(source?.efficacyLevel),
    physicalStress: asSeries(source?.physicalStress)
  };
};

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const HbAnalyticsPage = () => {
  const { companyId: paramCompanyId, branchId } = useParams();
  const { state } = useLocation();
  const companyId = paramCompanyId || state?.companyId;
  const analyticsScopeId = branchId || companyId;
  const areaScopeId = analyticsScopeId;

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFilters, setSelectedFilters] = useState({
    ageRange: searchParams.get("ageRange") || "",
    residenceState: searchParams.get("residenceState") || "",
    workScheme: searchParams.get("workScheme") || "",
    biologicalSex: searchParams.get("biologicalSex") || "",
    jobArea: searchParams.get("jobArea") || ""
  });

  const [companyName, setCompanyName] = useState({});
  const [companyJobAreas, setCompanyJobAreas] = useState([]);
  const [hraAnalyticsData, setHraAnalyticsData] = useState(null);
  const [stressAnalyticsData, setStressAnalyticsData] = useState(null);
  const [initialTotal, setInitialTotal] = useState(null);
  const [loading, setLoading] = useState(true);

  const [respondents, setRespondents] = useState([]);
  const [loadingRespondents, setLoadingRespondents] = useState(true);
  const [empSortBy, setEmpSortBy] = useState("email");
  const [empSortOrder, setEmpSortOrder] = useState("asc");

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;
  const [exporting, setExporting] = useState(false);

  const jobAreaOptions = companyJobAreas.length ? companyJobAreas : jobArea;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const next = { ...selectedFilters, [name]: value };
    setSelectedFilters(next);

    const nextParams = new URLSearchParams(searchParams);
    if (value) nextParams.set(name, value);
    else nextParams.delete(name);
    setSearchParams(nextParams);

    setCurrentPage(1);
  };

  useEffect(() => {
    if (!areaScopeId) return;
    (async () => {
      try {
        const scope = await questionnaireScopeApi.getById(areaScopeId);
        setCompanyName(scope);
        setCompanyJobAreas(scope.jobAreas || []);
      } catch (error) {
        console.error("Error cargando empresa/sucursal:", error);
      }
    })();
  }, [areaScopeId]);

  useEffect(() => {
    if (!analyticsScopeId) return;

    (async () => {
      setLoading(true);
      try {
        const [hraRaw, stressRaw] = await Promise.all([
          hbAnalyticsApi.getHbHraAnalyticsByCompany(analyticsScopeId, selectedFilters),
          hbAnalyticsApi.getHbStressAnalyticsByCompany(analyticsScopeId, selectedFilters)
        ]);
        const hraData = normalizeHraAnalytics(hraRaw);
        const stressData = normalizeStressAnalytics(stressRaw);

        if (initialTotal === null) {
          setInitialTotal(
            hraData?.totalRespondents ?? stressData?.totalRespondents ?? 0
          );
        }

        setHraAnalyticsData(hraData);
        setStressAnalyticsData(stressData);
      } catch (error) {
        console.error("Error cargando analíticas HB:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [analyticsScopeId, selectedFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!analyticsScopeId) return;

    (async () => {
      setLoadingRespondents(true);
      try {
        const list = await hbAnalyticsApi.getHbRespondantsByCompanyId(analyticsScopeId);
        setRespondents(list);
      } catch (error) {
        console.error("Error cargando respondants HB:", error);
      } finally {
        setLoadingRespondents(false);
      }
    })();
  }, [analyticsScopeId]);

  const sortedRespondents = useMemo(() => {
    return [...respondents].sort((a, b) => {
      const va = a[empSortBy] ?? "";
      const vb = b[empSortBy] ?? "";
      if (va < vb) return empSortOrder === "asc" ? -1 : 1;
      if (va > vb) return empSortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [respondents, empSortBy, empSortOrder]);

  const totalPages = Math.ceil(sortedRespondents.length / pageSize) || 1;
  const displayedRespondents = sortedRespondents.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const onSort = (column) => {
    if (empSortBy === column) {
      setEmpSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setEmpSortBy(column);
    setEmpSortOrder("asc");
  };

  const handleDelete = async (responseId) => {
    if (!responseId) return;
    try {
      await responsesApi.deleteHBResponse(responseId);
      setRespondents((prev) => prev.filter((r) => r.responseId !== responseId));
    } catch (error) {
      console.error("Error eliminando respuesta HB:", error);
      alert("No se pudo eliminar la respuesta.");
    }
  };

  const handleDownloadExcel = async () => {
    if (!analyticsScopeId) return;
    try {
      setExporting(true);
      await hbAnalyticsApi.downloadHbRespondantsByCompanyIdExcel(analyticsScopeId);
    } catch (error) {
      console.error("Error exportando respondants HB:", error);
    } finally {
      setExporting(false);
    }
  };

  const SortArrow = ({ column }) => {
    if (empSortBy !== column) {
      return (
        <span className={styles.sortArrow}>
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="4,0 8,8 0,8" fill="currentColor" />
          </svg>
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="0,0 8,0 4,8" fill="currentColor" />
          </svg>
        </span>
      );
    }
    return (
      <span className={styles.sortArrow}>
        {empSortOrder === "asc" ? (
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="4,0 8,8 0,8" fill="currentColor" />
          </svg>
        ) : (
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="0,0 8,0 4,8" fill="currentColor" />
          </svg>
        )}
      </span>
    );
  };

  const renderFilter = (label, name, options) => (
    <div className={styles.filterItem} key={name}>
      <label htmlFor={name} className={styles.filterLabel}>{label}</label>
      <select
        id={name}
        name={name}
        value={selectedFilters[name]}
        onChange={handleFilterChange}
        className={styles.select}
      >
        <option value="">Todos</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );

  const activeFilterCount = Object.values(selectedFilters).filter(Boolean).length;
  
    const clearFilters = () => {
      setSelectedFilters({
        jobArea: "",
        ageRange: "",
        residenceState: "",
        biologicalSex: "",
      });
      setSearchParams(new URLSearchParams());
      setCurrentPage(1);
    };

  const totalRespondents =
    hraAnalyticsData?.totalRespondents ?? stressAnalyticsData?.totalRespondents ?? respondents.length;

  const hasHraCharts =
    (hraAnalyticsData?.metabolicRisk?.length || 0) > 0 ||
    (hraAnalyticsData?.nutritionalState?.length || 0) > 0 ||
    (hraAnalyticsData?.lifeStyle?.length || 0) > 0 ||
    (hraAnalyticsData?.physicalActivityEvaluation?.length || 0) > 0 ||
    (hraAnalyticsData?.workHappiness?.length || 0) > 0 ||
    (hraAnalyticsData?.overallEvaluation?.length || 0) > 0;

  const hasStressCharts =
    (stressAnalyticsData?.hasBurnOut?.length || 0) > 0 ||
    (stressAnalyticsData?.exhaustionLevel?.length || 0) > 0 ||
    (stressAnalyticsData?.cynicismLevel?.length || 0) > 0 ||
    (stressAnalyticsData?.efficacyLevel?.length || 0) > 0 ||
    (stressAnalyticsData?.physicalStress?.length || 0) > 0;

  return (
    <div className={styles.page}>
      
      <header className={styles.header}>
        <div>
          <span className={styles.eyebrow}>ANALÍTICA DE BIENESTAR</span>
          <h2>HRA & Burnout</h2>
          <p className={styles.subheader}>
            Resultados de <span className={styles.companyId}>{companyName.name || "la empresa"}</span>
          </p>
        </div>
        <div className={styles.summaryCards}>
          {initialTotal !== null && (
            <div className={styles.summaryCard}>
              <span>Participantes iniciales</span>
              <strong>{initialTotal}</strong>
            </div>
          )}
          {!loading && totalRespondents && (
            <div className={`${styles.summaryCard} ${styles.summaryCardAccent}`}>
              <span>Resultados visibles</span>
              <strong>{totalRespondents}</strong>
            </div>
          )}
        </div>
      </header>

      <section className={styles.filtersPanel}>
        <div className={styles.filtersHeader}>
          <div>
            <h3>Filtros generales</h3>
            <p>Refina todas las gráficas al mismo tiempo.</p>
          </div>
          <button type="button" className={styles.clearFilters} onClick={clearFilters} disabled={activeFilterCount === 0}>
            Limpiar filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
        </div>
        <div className={styles.filters}>
          {renderFilter("Rango de Edad", "ageRange", ageRanges)}
          {renderFilter("Estado de Residencia", "residenceState", mexicoStates)}
          {renderFilter("Esquema Laboral", "workScheme", workScheme)}
          {renderFilter("Sexo Biológico", "biologicalSex", biologicalSexList)}
          {renderFilter("Área de Trabajo", "jobArea", jobAreaOptions)}
        </div>
      </section>

      <section className={styles.content}>
        <div className={styles.sectionHeading}>
          <div>
            <span className={styles.sectionEyebrow}>RESULTADOS</span>
            <h3>Indicadores de HRA & Burnout</h3>
          </div>
          {!loading && totalRespondents && <span className={styles.resultCount}>{totalRespondents} respuestas</span>}
        </div>
        {loading ? (
          <div className={styles.loadingCard}>Cargando gráficas...</div>
        ) : totalRespondents ? (
          <>
            {hasHraCharts && (
              <>
                <h3 style={{ marginBottom: "0.75rem" }}>Métricas HRA</h3>
                <div className={styles.charts}>
                  <BarChartComponent
                    data={hraAnalyticsData.metabolicRisk}
                    title="Riesgo Metabólico"
                    variant="dashboard"
                    twoLineLabels
                    semanticColors
                    downloadable
                  />
                  <BarChartComponent
                    data={hraAnalyticsData.nutritionalState}
                    title="Estado Nutricional"
                    variant="dashboard"
                    twoLineLabels
                    semanticColors
                    downloadable
                  />
                  <BarChartComponent
                    data={hraAnalyticsData.lifeStyle}
                    title="Estilo de Vida"
                    variant="dashboard"
                    twoLineLabels
                    semanticColors
                    downloadable
                  />
                  <BarChartComponent
                    data={hraAnalyticsData.physicalActivityEvaluation}
                    title="Actividad Física"
                    variant="dashboard"
                    twoLineLabels
                    semanticColors
                    downloadable
                  />
                  <BarChartComponent
                    data={hraAnalyticsData.workHappiness}
                    title="Felicidad Laboral"
                    variant="dashboard"
                    twoLineLabels
                    semanticColors
                    downloadable
                  />
                  <BarChartComponent
                    data={hraAnalyticsData.overallEvaluation}
                    title="Evaluación General"
                    variant="dashboard"
                    twoLineLabels
                    semanticColors
                    downloadable
                  />
                </div>
              </>
            )}

            {hasStressCharts && (
              <>
                <h3 style={{ margin: "1.25rem 0 0.75rem" }}>Métricas Estrés y Burnout</h3>
                <div className={styles.charts}>
                  <BarChartComponent
                    data={stressAnalyticsData.hasBurnOut}
                    title="Presencia de Burnout"
                    variant="dashboard"
                    twoLineLabels
                    semanticColors
                    downloadable
                  />
                  <BarChartComponent
                    data={stressAnalyticsData.exhaustionLevel}
                    title="Agotamiento Emocional"
                    variant="dashboard"
                    twoLineLabels
                    semanticColors
                    downloadable
                  />
                  <BarChartComponent
                    data={stressAnalyticsData.cynicismLevel}
                    title="Desconexión Laboral"
                    variant="dashboard"
                    twoLineLabels
                    semanticColors
                    downloadable
                  />
                  <BarChartComponent
                    data={stressAnalyticsData.efficacyLevel}
                    title="Eficiencia Laboral"
                    variant="dashboard"
                    twoLineLabels
                    semanticColors
                    downloadable
                  />
                  <BarChartComponent
                    data={stressAnalyticsData.physicalStress}
                    title="Estrés Físico"
                    variant="dashboard"
                    twoLineLabels
                    semanticColors
                    downloadable
                  />
                </div>
              </>
            )}
          </>
        ) : (
          <p className={styles.message}>No se pudieron cargar los datos.</p>
        )}
      </section>

      {/* Listado de Empleados (Respondants) */}
      <section className={styles.responsesSection}>
        <div className={styles.tableHeader}>
          <div>
            <span className={styles.sectionEyebrow}>DETALLE</span>
            <h3>Listado de empleados</h3>
            <p>{respondents.length} empleados registrados</p>
          </div>
          <button
            className={styles.exportButton}
            onClick={handleDownloadExcel}
            disabled={exporting || !analyticsScopeId}
            title="Exportar listado de empleados (Estrés) a Excel"
          >
            {exporting ? "Descargando..." : "Exportar Excel"}
          </button>
        </div>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th onClick={() => onSort("email")} className={styles.clickable}>
                  Correo <SortArrow column="email" />
                </th>
                <th onClick={() => onSort("status")} className={styles.clickable}>
                  Estado <SortArrow column="status" />
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedRespondents.length > 0 ? (
                displayedRespondents.map((r, i) => (
                  <tr key={r.email}>
                    <td>{(currentPage - 1) * pageSize + i + 1}</td>
                    <td>{r.email}</td>
                    <td>{r.status}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className={styles.message}>
                    No hay empleados para mostrar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Controles de paginación + botón Excel */}
        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            Anterior
          </button>
          <span>
            Página {currentPage} de {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Siguiente
          </button>
        </div>
      </section>
    </div>
  );
};

export default HbAnalyticsPage;
